import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Input } from "@vds/inputs";

const TypeaheadDropdown = ({
  width = "100%",
  dropdownWidth = null,
  type,
  maxLength = 255,
  data = [],
  value,
  onChange,
  label,
  error,
  errorText,
  disabled,
  onFocus = () => {},
  onBlur = () => {},
  validate = () => true,
  placeholderColor = "gray",
  showDropdownForSingleOption = true,
  debounceDelay = 100,
}) => {
  const [isPlaceholder, setIsPlaceholder] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(value);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, debounceDelay);

    return () => {
      clearTimeout(handler); // Clear timeout if value changes before delay
    };
  }, [value, debounceDelay]);

  const handleFocus = (e) => {
    if (isPlaceholder) {
      onChange("");
      setIsPlaceholder(false);
    }
    setShowDropdown(true);
    onFocus();
  };

  const handleBlur = (e) => {
    if (e.relatedTarget?.classList?.contains("typeahead-dropdown-item")) return;
    setShowDropdown(false);
    onBlur();
  };

  const handleOptionClick = (value) => {
    setShowDropdown(false);
    onChange(value);
  };

  const dropdownData = useMemo(() => {
    if (!data?.length) return [];
    return data?.filter((q) => q.toString().startsWith(debouncedValue));
  }, [data, debouncedValue]);

  return (
    <div>
      <DropdownWrapper width={width}>
        <Input
          label={label}
          width={width}
          type={type}
          maxLength={maxLength}
          value={value}
          onChange={(e) => {
            const val = e.target.value.trim();
            if (validate(val)) {
              onChange(val); // Update the value immediately
              setShowDropdown(true);
            }
          }}
          style={{
            color:
              isPlaceholder && placeholderColor ? placeholderColor : "black",
          }}
          onFocus={(e) => handleFocus(e)}
          onBlur={(e) => handleBlur(e)}
          error={!!error}
          errorText={errorText}
          disabled={disabled}
        />
      </DropdownWrapper>
      {!!showDropdown &&
        !!dropdownData?.length &&
        (dropdownData?.length > 1 || showDropdownForSingleOption) && (
          <DropdownList role="listbox" width={dropdownWidth ?? width}>
            {dropdownData.map((q) => (
              <li
                key={q}
                className="typeahead-dropdown-item"
                tabIndex="0"
                role="option"
                onClick={() => handleOptionClick(q)}
              >
                {q}
              </li>
            ))}
          </DropdownList>
        )}
    </div>
  );
};

TypeaheadDropdown.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dropdownWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  type: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  validate: PropTypes.func,
  placeholderColor: PropTypes.string,
  showDropdownForSingleOption: PropTypes.bool,
  debounceDelay: PropTypes.number,
};

const DropdownWrapper = styled.div`
  width: ${({ width }) =>
    Number.isInteger(width) ? `${width}px` : `${width}`};
  height: 4rem;
  padding-top: 10px;
`;

const DropdownList = styled.ul`

::-webkit-scrollbar-thumb {
  background-color: grey;
  border-radius: 3px;
}
::-webkit-scrollbar {
  width: 05px;
  border-radius: 6px;
  background-color: #eeee;
}

 position: absolute;
  width: ${({ width }) =>
    Number.isInteger(width) ? `${width}px` : `${width}`};
  max-height: 200px;
  overflow-y: auto;
  border-radius: 4px;
  background-color: white;
  z-index: 10;
  list-style-type: none;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: -10px;
  padding-left: 0;
  > li {
    padding: 10px;
    cursor: pointer;
    &:hover {
      background-color: #f0f0f0;
    }
  }
`;

export default TypeaheadDropdown;
